import Spinner from "@/components/spinner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useCompanyById } from "@/hooks/company-by-id.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useNewUser } from "@/hooks/new-user.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { Add } from "iconsax-react";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { formSchema } from "./new-user-form.schema";

const NewUserForm: FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
    },
  });

  const { data: userProfile } = useCurrentUserProfile();

  const { data: company } = useCompanyById(userProfile?.companyId || "");

  const mutation = useNewUser();

  const resetAndClose = () => {
    form.reset();
    mutation.reset();
    setIsOpen(false);
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    mutation.mutate({
      companyId: company!.id,
      ...values,
    });
  };

  const handleCancel = () => {
    resetAndClose();
  };

  useEffect(() => {
    if (mutation.isSuccess) {
      resetAndClose();
    }
  }, [mutation.isSuccess]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="secondary" size="sm">
          <Add size={24} /> Nuevo usuario
        </Button>
      </SheetTrigger>

      <SheetContent className="flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle>Nuevo usuario</SheetTitle>
          <SheetDescription className="hidden">
            Añade un nuevo usuario a la empresa
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="mt-4 flex flex-col gap-6"
            >
              <h3 className="text-base font-bold text-[#615CB7]">
                Información general
              </h3>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre y Apellido*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Correo*</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="sticky bottom-0 mt-6 grid grid-cols-2 items-center gap-2 bg-white pb-4 pt-2">
                <Button
                  type="submit"
                  variant="default"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending && <Spinner className="mr-2" />}
                  Crear usuario
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </Form>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};

export default NewUserForm;
